import { FC, useState } from "react";
import { cx } from "@jugl-web/utils";
import { InteractiveContainer } from "@jugl-web/ui-kit";
import { isVideoUrl } from "@jugl-web/utils/utils/files";
import { ReactComponent as PlayIcon } from "./assets/play.svg";

const MAX_VISIBLE = 4;

interface CommentMediaGridProps {
  attachments: string[];
  onAttachmentClick?: (url: string, allUrls: string[]) => void;
}

export const CommentMediaGrid: FC<CommentMediaGridProps> = ({
  attachments,
  onAttachmentClick,
}) => {
  const visibleAttachments = attachments.slice(0, MAX_VISIBLE);
  const extraCount = attachments.length - MAX_VISIBLE;
  const [previewStates, setPreviewStates] = useState<
    Record<number, "loading" | "error" | "loaded">
  >({});

  return (
    <div
      className={cx(
        "mt-2 overflow-hidden rounded-lg",
        visibleAttachments.length > 1
          ? "grid auto-rows-[160px] grid-cols-2 gap-1"
          : ""
      )}
    >
      {visibleAttachments.map((url, index) => {
        const isVideo = isVideoUrl(url);
        const previewState = previewStates[index] || "loading";
        const isSingle = visibleAttachments.length === 1;
        const isTripleFirst = visibleAttachments.length === 3 && index === 0;

        return (
          <InteractiveContainer
            key={+index}
            isDisabled={!onAttachmentClick}
            onClick={() => onAttachmentClick?.(url, attachments)}
            className={cx(
              "relative flex items-center justify-center overflow-hidden bg-gradient-to-b from-[#F4F6F7] to-[#CDE2F2]",
              isTripleFirst && "row-span-2",
              isSingle ? "h-[260px] max-w-full rounded-lg" : "min-h-[160px]"
            )}
          >
            {previewState === "loading" && !isVideo && (
              <div className="absolute inset-0 animate-pulse bg-grey-200" />
            )}

            {isVideo && (
              <div className="absolute inset-0 z-10 flex items-center justify-center">
                <PlayIcon />
              </div>
            )}

            {isVideo ? (
              <video
                src={`${url}#t=0.001`}
                className="h-full w-full object-cover"
                muted
                preload="metadata"
                onLoadedMetadata={() =>
                  setPreviewStates((prev) => ({ ...prev, [index]: "loaded" }))
                }
                onError={() =>
                  setPreviewStates((prev) => ({ ...prev, [index]: "error" }))
                }
              />
            ) : (
              <img
                src={url}
                alt=""
                className={cx("h-full w-full object-contain", {
                  invisible: previewState === "loading",
                })}
                onLoad={() =>
                  setPreviewStates((prev) => ({ ...prev, [index]: "loaded" }))
                }
                onError={() =>
                  setPreviewStates((prev) => ({ ...prev, [index]: "error" }))
                }
              />
            )}

            {index === MAX_VISIBLE - 1 && extraCount > 0 && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 font-secondary text-base text-white">
                +{extraCount} Files
              </div>
            )}
          </InteractiveContainer>
        );
      })}
    </div>
  );
};
